<template>
  <div class="ClientPerformanceChart">
    <AppLoading v-if="loading" />

    <!-- ================================================================= -->
    <!-- EMPTY STATE 1 of 2 — NOT AUTHORISED (HTTP 401)                    -->
    <!-- An agent asking for a client that is not theirs. This is a        -->
    <!-- permission answer: the curve exists, it is simply not yours.      -->
    <!-- ================================================================= -->
    <div v-else-if="notAuthorised" class="ClientPerformanceChart__state ClientPerformanceChart__state--denied">
      <div class="ClientPerformanceChart__state__icon">
        <i class="bi bi-shield-lock" />
      </div>
      <div class="ClientPerformanceChart__state__title">
        Not authorised
      </div>
      <div class="ClientPerformanceChart__state__text">
        This account is not assigned to you, so its performance history is not available here.
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- EMPTY STATE 2 of 2 — NO HISTORY YET (chartable: false)            -->
    <!-- Deliberately NOT the same block as above. This one says the data  -->
    <!-- does not exist yet; the one above says it exists and is withheld. -->
    <!-- Confusing the two would tell a client he has no history when the  -->
    <!-- truth is that his agent may not read it.                          -->
    <!-- ================================================================= -->
    <div v-else-if="payload && !payload.chartable" class="ClientPerformanceChart__state ClientPerformanceChart__state--empty">
      <div class="ClientPerformanceChart__state__icon">
        <i class="bi bi-graph-up" />
      </div>
      <div class="ClientPerformanceChart__state__title">
        No performance history yet
      </div>
      <div class="ClientPerformanceChart__state__text">
        {{ emptyReasonText }}
      </div>
      <div class="ClientPerformanceChart__state__detail" v-if="payload.detail">
        {{ payload.detail }}
      </div>
    </div>

    <div v-else-if="failed" class="ClientPerformanceChart__state ClientPerformanceChart__state--empty">
      <div class="ClientPerformanceChart__state__icon">
        <i class="bi bi-exclamation-triangle" />
      </div>
      <div class="ClientPerformanceChart__state__title">
        Performance unavailable
      </div>
      <div class="ClientPerformanceChart__state__text">
        The performance history could not be loaded. Please try again in a moment.
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- THE CHART                                                         -->
    <!-- ================================================================= -->
    <div v-else-if="chartData">
      <div class="ClientPerformanceChart__cumulative" :class="{ 'is-negative': payload.client.cumulative < 0 }">
        {{ formatSignedPercent(payload.client.cumulative) }}
      </div>
      <div class="ClientPerformanceChart__since">
        since {{ formatDate(payload.client.inception) }}
      </div>

      <div class="ClientPerformanceChart__chart">
        <AppChart type="line" :data="chartData" :options="chartOptions" />
      </div>

      <!-- ---------------------------------------------------------------- -->
      <!-- TIER 1 DISCLOSURE — always visible.                              -->
      <!-- The only sentence that changes how the chart READS: without it a -->
      <!-- client compares his net-of-fees curve against gross indices and  -->
      <!-- concludes we underperform.                                       -->
      <!-- ---------------------------------------------------------------- -->
      <div class="ClientPerformanceChart__disclosure">
        <div class="ClientPerformanceChart__disclosure__headline">
          <span>Your performance is net of fees. Indices are price-only.</span>

          <button
            type="button"
            class="ClientPerformanceChart__disclosure__toggle btn btn-link btn-sm"
            :aria-expanded="showMethodology ? 'true' : 'false'"
            @click="showMethodology = !showMethodology"
          >
            <i class="bi" :class="showMethodology ? 'bi-info-circle-fill' : 'bi-info-circle'" />
            <span class="visually-hidden">Methodology and sources</span>
          </button>
        </div>

        <!-- TIER 2 — collapsed. Detail that a client can ask for, not detail
             he must read to interpret the picture correctly. -->
        <div class="ClientPerformanceChart__methodology" v-if="showMethodology">
          <dl>
            <dt>Method</dt>
            <dd>{{ methodology.method }}</dd>

            <dd class="ClientPerformanceChart__methodology__sub" v-if="methodology.month_boundary_rule">
              Month boundary: {{ methodology.month_boundary_rule }}.
            </dd>
            <dd class="ClientPerformanceChart__methodology__sub" v-if="methodology.annual_basis">
              Annual figures: {{ methodology.annual_basis }}.
            </dd>

            <dt>Sources</dt>
            <dd>
              S&amp;P 500 via FRED, BTC via Binance, gold via PAXG — a gold-backed token,
              not the LBMA fix.
            </dd>
            <dd class="ClientPerformanceChart__methodology__sub" v-if="methodology.benchmark_basis">
              Indices are {{ methodology.benchmark_basis }}<template v-if="methodology.benchmarks_net_of_fees === false">, and unlike your own curve they carry no fees</template>.
            </dd>
            <dd class="ClientPerformanceChart__methodology__sub">
              Gold history starts in August 2020, so it is absent for earlier inceptions.
            </dd>
            <dd class="ClientPerformanceChart__methodology__sub" v-for="note in unavailableBenchmarkNotes" :key="note">
              {{ note }}
            </dd>

            <template v-if="hasUnmeasured">
              <dt>Unmeasured intervals</dt>
              <dd>
                A dashed segment is an interval with no valuation on record. The line is drawn
                across it so the chart stays continuous, but nothing was observed in between.
              </dd>
            </template>

            <template v-if="methodology.comparison_note">
              <dt>Reading the comparison</dt>
              <dd>{{ methodology.comparison_note }}</dd>
            </template>
          </dl>

          <p class="ClientPerformanceChart__methodology__warning">
            Past performance is not indicative of future results.
          </p>
        </div>
      </div>

      <!-- ---------------------------------------------------------------- -->
      <!-- ANNUAL TABLE — benchmark columns follow the checkboxes exactly.  -->
      <!-- ---------------------------------------------------------------- -->
      <div class="ClientPerformanceChart__annual" v-if="payload.annual && payload.annual.length">
        <div class="table-responsive">
          <table class="ClientPerformanceChart__table table table-sm">
            <thead>
              <tr>
                <th>Year</th>
                <th class="text-end">Your performance</th>
                <th class="text-end" v-for="key in activeBenchmarks" :key="key">
                  {{ BENCHMARKS[key].label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in payload.annual" :key="row.year">
                <td>
                  {{ row.label }}
                  <span class="ClientPerformanceChart__badge" v-if="row.partial" title="This year is not covered end to end">
                    partial
                  </span>
                  <span class="ClientPerformanceChart__badge ClientPerformanceChart__badge--warn" v-if="row.has_unmeasured" title="Part of this year has no valuation on record">
                    unmeasured
                  </span>
                </td>
                <td class="text-end" :class="signClass(row.client)">
                  {{ formatSignedPercent(row.client) }}
                </td>
                <td class="text-end" :class="signClass(row[key])" v-for="key in activeBenchmarks" :key="key">
                  {{ formatSignedPercent(row[key]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import AppChart from '@/components/base/AppChart'
  import AppLoading from '@/components/AppLoading'

  import performanceService from '@/services/performance.service'

  import theTheme from '@/theme.js'
  import { BENCHMARKS, BENCHMARK_KEYS } from '@/utils/benchmarks'

  /* -------------------------------------------------------------------------------
     NUMBER FORMATTING — DELIBERATELY LOCAL TO THIS COMPONENT.

     The app-wide formatDecimal() in utils/number.js is fr-FR ("12,34"), and it is what
     renders invoices and AppValue. Switching it to a decimal point would rewrite billing
     documents, so this chart carries its own en-US formatters instead and the rest of the
     app is left exactly as it is. Axis, tooltips and the annual table all read from here,
     so the three cannot drift apart either.
     ------------------------------------------------------------------------------- */

  const PERCENT = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  const PERCENT_AXIS = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
  })

  /* Why a chart has nothing to draw, in the client's language. The server's own `detail`
     is rendered underneath, so this map only has to cover the headline. */

  const EMPTY_REASONS = {
    NOT_STARTED: 'This account has not started trading yet.',
    NO_MEASURABLE_HISTORY: 'No valuation has been recorded yet. The curve appears after the first full month.',
    BELOW_MINIMUM_CAPITAL: 'This account has not held enough capital for a percentage return to be meaningful.',
    DATA_EXCLUDED: 'This account holds a record that this calculation will not interpret.'
  }

  /* A benchmark the server could not sample sends back null, which is a real answer
     ("no close on that date") and must render as an em dash, not as 0%. */

  function isBlank(value) {
    return typeof value === 'undefined' || value === null
  }

  export default {
    components: {
      AppChart,
      AppLoading
    },

    props: {

      /* Series keys currently ticked in the "Compare with" block, owned by the parent.
         Everything they need is already in the payload, so reacting to them is pure
         client-side work — no refetch, which is what makes ticking a box feel instant. */

      compare: {
        type: Array,
        default: () => []
      }
    },

    data() {
      return {
        BENCHMARKS,

        loading: true,
        failed: false,
        notAuthorised: false,
        payload: null,
        theme: null,

        showMethodology: false
      }
    },

    computed: {
      methodology() {
        return this.payload && this.payload.methodology || {}
      },

      /* Only benchmarks that are both ticked AND actually present in the payload. GOLD is
         absent for any client whose inception predates August 2020. */

      activeBenchmarks() {
        if (!this.payload || !this.payload.benchmarks) {
          return []
        }

        return BENCHMARK_KEYS.filter((key) => {
          const bench = this.payload.benchmarks[key]

          return this.compare.includes(key) && bench && bench.available
        })
      },

      /* A ticked benchmark the server could not rebase says so, in its own words, rather
         than silently drawing nothing. */

      unavailableBenchmarkNotes() {
        if (!this.payload || !this.payload.benchmarks) {
          return []
        }

        return BENCHMARK_KEYS.filter((key) => {
          const bench = this.payload.benchmarks[key]

          return bench && !bench.available && bench.reason
        }).map((key) => {
          return BENCHMARKS[key].label + ' is not shown: ' + this.payload.benchmarks[key].reason + '.'
        })
      },

      clientSeries() {
        return this.payload && this.payload.client && this.payload.client.series || []
      },

      hasUnmeasured() {
        return this.clientSeries.some((point) => point.measured === false)
      },

      emptyReasonText() {
        return EMPTY_REASONS[this.payload.reason] || 'There is not enough history to draw a curve yet.'
      },

      chartData() {
        if (!this.payload || !this.payload.chartable || !this.theme) {
          return null
        }

        const datasets = [
          {
            label: 'Your performance',
            data: this.clientSeries.map((point) => {
              return { x: new Date(point.date).toISOString(), y: this.toPercent(point.cumulative) }
            }),
            borderColor: this.theme.primary,
            backgroundColor: this.theme.primary,
            pointBackgroundColor: this.theme.primary,
            borderWidth: 3,
            pointRadius: 2,
            pointBorderWidth: 0,
            fill: false,
            cubicInterpolationMode: 'monotone',

            /* ------------------------------------------------------------------
               UNMEASURED INTERVALS.
               A point carries measured:false when the interval ENDING at it has no
               valuation on record, so the segment to dash is the one arriving at
               that point — hence p1DataIndex, not p0DataIndex.
               Drawing this solid would assert we observed a value we never
               observed; across a long gap that is a straight invented line.
               [] is Chart.js's explicit "solid" for the measured case.
               ------------------------------------------------------------------ */

            segment: {
              borderDash: (ctx) => this.isUnmeasuredAt(ctx.p1DataIndex) ? [ 6, 5 ] : []
            }
          }
        ]

        this.activeBenchmarks.forEach((key) => {
          const bench = this.payload.benchmarks[key]
          const color = this.theme[BENCHMARKS[key].themeKey]

          datasets.push({
            label: BENCHMARKS[key].label,
            data: bench.series.map((point) => {
              return { x: new Date(point.date).toISOString(), y: this.toPercent(point.cumulative) }
            }),
            borderColor: color,
            backgroundColor: color,
            pointBackgroundColor: color,
            borderWidth: 2,
            pointRadius: 0,
            pointBorderWidth: 0,
            fill: false,
            cubicInterpolationMode: 'monotone'
          })
        })

        return { datasets }
      },

      chartOptions() {
        return {
          maintainAspectRatio: false,
          responsive: true,
          interaction: {
            mode: 'index',
            intersect: false
          },
          plugins: {

            /* Enabled here and nowhere else in this codebase: this is the only chart that
               ever draws more than one series, so it is the only one where a legend
               carries information rather than repeating the title. */

            legend: {
              display: true,
              position: 'bottom',
              labels: {
                usePointStyle: true,
                boxWidth: 8
              }
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const raw = ctx.parsed.y

                  if (isBlank(raw)) {
                    return ctx.dataset.label + ': —'
                  }

                  return ctx.dataset.label + ': ' + this.formatSignedPercent(raw / 100)
                },
                footer: (items) => {
                  const hit = items.find((item) => item.datasetIndex === 0)

                  if (hit && this.isUnmeasuredAt(hit.dataIndex)) {
                    return 'No valuation on record for this interval.'
                  }

                  return ''
                }
              }
            }
          },
          scales: {
            x: {
              type: 'time',
              time: {
                unit: 'month'
              }
            },
            y: {
              ticks: {
                callback: (value) => PERCENT_AXIS.format(value) + '%'
              }
            }
          }
        }
      }
    },

    async mounted() {
      this.theme = await theTheme.ready()
      this.load()
    },

    methods: {
      async load() {
        this.loading = true
        this.failed = false
        this.notAuthorised = false

        try {
          this.payload = await performanceService.fetch()
        } catch (error) {
          /* 401 is the enforced agent-ownership guard, not a dead session — see
             performance.service.js for why it never reaches the global interceptor. */

          if (error && error.status === 401) {
            this.notAuthorised = true
          } else {
            this.failed = true
          }
        }

        this.loading = false
      },

      isUnmeasuredAt(index) {
        const point = this.clientSeries[index]

        return !!point && point.measured === false
      },

      toPercent(value) {
        return isBlank(value) ? null : value * 100
      },

      formatSignedPercent(value) {
        if (isBlank(value)) {
          return '—'
        }

        const pct = value * 100

        return (pct > 0 ? '+' : '') + PERCENT.format(pct) + '%'
      },

      signClass(value) {
        if (isBlank(value)) {
          return null
        }

        return value < 0 ? 'is-negative' : 'is-positive'
      },

      formatDate(ymd) {
        if (!ymd) {
          return ''
        }

        return new Date(ymd).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })
      }
    }
  }
</script>

<style lang="sass" scoped>
  @import '@/sass/styles.vars.all.sass'

  .ClientPerformanceChart
    position: relative

  .ClientPerformanceChart__cumulative
    font-size: 2em
    font-weight: bold
    color: $primary
    line-height: 1.1

    &.is-negative
      color: $danger

  .ClientPerformanceChart__since
    font-size: .85em
    color: $text-muted
    margin-bottom: $spacer

  .ClientPerformanceChart__chart
    height: 300px

  // --- empty states -----------------------------------------------------------
  // The two states are given different iconography and different colour weight on
  // purpose: they answer opposite questions ("may I?" vs "is there any?").

  .ClientPerformanceChart__state
    display: flex
    flex-direction: column
    justify-content: center
    align-items: center
    text-align: center
    height: 340px
    padding: $spacer-3

  .ClientPerformanceChart__state__icon
    font-size: 3rem
    line-height: 1
    margin-bottom: $spacer-2

  .ClientPerformanceChart__state__title
    font-size: 1.25rem
    font-weight: bold
    margin-bottom: $spacer-1

  .ClientPerformanceChart__state__text
    max-width: 32em
    color: $text-muted

  .ClientPerformanceChart__state__detail
    max-width: 32em
    margin-top: $spacer-2
    font-size: .8em
    font-style: italic
    color: $text-muted

  .ClientPerformanceChart__state--denied
    .ClientPerformanceChart__state__icon
      color: $tertiary

    .ClientPerformanceChart__state__title
      color: $tertiary

  .ClientPerformanceChart__state--empty
    .ClientPerformanceChart__state__icon
      color: $gray-400

    .ClientPerformanceChart__state__title
      color: $gray-600

  // --- disclosure -------------------------------------------------------------

  .ClientPerformanceChart__disclosure
    margin-top: $spacer-3
    padding-top: $spacer-2
    border-top: 1px solid $border-color

  .ClientPerformanceChart__disclosure__headline
    display: flex
    align-items: center
    justify-content: center
    gap: $spacer-1
    font-size: .85em
    color: $gray-700

  .ClientPerformanceChart__disclosure__toggle
    padding: 0
    line-height: 1
    text-decoration: none
    color: $secondary

  .ClientPerformanceChart__methodology
    margin: $spacer-2 auto 0
    max-width: 46em
    font-size: .8em
    color: $text-muted

    dt
      font-weight: bold
      color: $gray-700
      margin-top: $spacer-2

      &:first-child
        margin-top: 0

    dd
      margin-bottom: 0

  .ClientPerformanceChart__methodology__sub
    opacity: .85

  .ClientPerformanceChart__methodology__warning
    margin-top: $spacer-2
    margin-bottom: 0
    font-style: italic

  // --- annual table -----------------------------------------------------------

  .ClientPerformanceChart__annual
    margin-top: $spacer-3

  .ClientPerformanceChart__table
    font-size: .9em

    th
      color: $primary
      white-space: nowrap

    td.is-positive
      color: $success

    td.is-negative
      color: $danger

  .ClientPerformanceChart__badge
    display: inline-block
    margin-left: $spacer-1
    padding: .05em .5em
    border-radius: 1em
    font-size: .7em
    text-transform: uppercase
    letter-spacing: .03em
    background: $gray-200
    color: $gray-700

    &--warn
      background: tint-color($tertiary, 80)
      color: shade-color($tertiary, 25)
</style>
